/**
 * Transport simulato per lo sviluppo, quando non c'e' un backend configurato.
 *
 * Serve a rendere la chat realmente usabile in `yarn start`: input, attesa,
 * streaming token per token, fonti, cooldown ed errori si comportano come con
 * il backend vero, cosi' la UX si puo' provare end-to-end prima che
 * l'infrastruttura esista.
 *
 * SICUREZZA: questo modulo non deve MAI servire risposte in produzione. Una
 * risposta inventata su un CV, mostrata a un recruiter come se fosse reale, e'
 * un danno concreto. Oltre alla condizione in isMockMode(), ogni funzione
 * verifica NODE_ENV e solleva: se un refactoring dovesse collegarla per errore,
 * fallisce in modo rumoroso invece di mentire.
 *
 * Comandi deterministici per il test dei casi limite (l'ordinario e' casuale):
 *   /slow    risposta molto lenta, attiva il messaggio di cold start prolungato
 *   /deny    rifiuto del guardrail (dominio privato)
 *   /filter  richiesta filtrata: risponde solo alla parte ammessa
 *   /limit   errore 429 con countdown di cooldown
 *   /error   errore di generazione a metà stream
 *   /budget  quota mensile esaurita
 */

import { ChatError, ChatMeta, ChatPersona, StreamChatOptions, StreamEvent } from './chatTypes';

const MOCK_RATE_LIMIT_WINDOW_SECONDS = 10;

const assertNotProduction = (): void => {
  if (process.env.NODE_ENV === 'production') {
    throw new Error(
      'mockTransport non e utilizzabile in produzione: servirebbe risposte inventate.'
    );
  }
};

export const isMockMode = (): boolean =>
  process.env.NODE_ENV !== 'production' && !process.env.REACT_APP_CV_CHAT_API;

const sleep = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new ChatError('Richiesta annullata.', 'aborted'));
      return;
    }
    const timer = window.setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      window.clearTimeout(timer);
      reject(new ChatError('Richiesta annullata.', 'aborted'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });

const pick = <T>(items: readonly T[]): T => items[Math.floor(Math.random() * items.length)];

const jitter = (base: number, spread: number): number =>
  Math.max(0, base + (Math.random() - 0.5) * spread);

/** Ritardo del primo messaggio: simula il cold start di un container a zero repliche. */
const coldStartMs = (): number => {
  const configured = Number(process.env.REACT_APP_CV_CHAT_MOCK_COLDSTART_MS);
  return Number.isFinite(configured) && configured > 0 ? configured : 3200;
};

// --------------------------------------------------------------------------- //
// Contenuti simulati
// --------------------------------------------------------------------------- //

interface MockAnswer {
  keywords: RegExp;
  sources: string[];
  formal: string[];
  informal: string[];
}

const ANSWERS: MockAnswer[] = [
  {
    keywords: /azure|aws|cloud|terraform|infrastrutt|infrastructure/i,
    sources: ['WORK-OPENLOGS-02', 'SKILLS-04', 'CERT-05'],
    formal: [
      'Nel ruolo attuale di ICT Architect ho progettato una pipeline di ingestion cloud-native che porta dati di mercato e telemetria da AWS verso Snowflake, con architettura Medallion su Lambda, S3 e Parquet, interamente provisionata con Terraform. Su Azure ho lavorato con Azure DevOps e le relative pipeline CI/CD nel periodo in Swatch Group, e ho conseguito la certificazione Azure Administrator Associate nel 2026.',
      'L esperienza cloud e multi-cloud: Azure dal periodo in Swatch Group, con Azure DevOps e pipeline di rilascio, e AWS nel ruolo attuale, dove ho realizzato una pipeline di data ingestion verso Snowflake provisionata con Terraform. Accanto alla piattaforma ho condotto un programma di hardening che ha ridotto del 18 per cento i costi AWS.',
    ],
    informal: [
      'Sul cloud mi muovo su due fronti: Azure lo uso dai tempi di Swatch, soprattutto Azure DevOps per i rilasci, mentre su AWS ci lavoro adesso. Il pezzo di cui vado piu orgoglioso e la pipeline che porta dati di mercato da AWS a Snowflake: architettura Medallion, Lambda, S3, Parquet, tutto provisionato con Terraform. Nel farlo abbiamo anche tagliato il 18 per cento dei costi AWS.',
      'Cloud si, e con le mani dentro: la pipeline AWS verso Snowflake l ho progettata e scritta io, Terraform compreso. Su Azure vengo da Azure DevOps e dalle pipeline CI/CD di Swatch, e a inizio 2026 ho preso la certificazione Azure Administrator Associate per formalizzare la parte sistemistica.',
    ],
  },
  {
    keywords: /database|sql|dato|dati|bi\b|business intelligence|etl|snowflake/i,
    sources: ['SKILLS-03', 'WORK-ATG-01', 'CERT-02'],
    formal: [
      'Il dato e l area in cui ho la maggiore densita di competenze di livello massimo: SQL Server, T-SQL, ottimizzazione e amministrazione di database, SSIS e SSRS. Nel periodo in ATG ho ricoperto il ruolo di Database Developer e BI Data Scientist per clienti come IBM, ENI, SIA e Brembo, con dashboard in Tibco Spotfire dalla versione 6.5 alla 8. Nel 2018 ho conseguito la certificazione MCP Exam 461 su Querying Microsoft SQL Server.',
    ],
    informal: [
      'Il database e casa mia: SQL Server e T-SQL a livello esperto, piu ottimizzazione e amministrazione. In ATG facevo il Database Developer per clienti come IBM, ENI e Brembo, e costruivo dashboard in Spotfire con dentro anche alberi decisionali. La certificazione MCP 461 sul querying di SQL Server l ho presa nel 2018 per mettere un timbro su quello che facevo tutti i giorni.',
    ],
  },
  {
    keywords: /risultat|metric|numer|percentual|result|impact|kpi/i,
    sources: ['FAQ-03', 'WORK-OLTREBASE-02', 'WORK-SWATCH-04'],
    formal: [
      'I risultati misurabili documentati: in Oltrebase circa 110.000 righe di codice nel primo anno per un refactoring completo, con riduzione del 40 per cento del downtime e miglioramento del 90 per cento della resilienza del sistema distribuito. In Swatch Group il progetto Logbook ha portato la qualita dei dati di produzione a un miglioramento di circa il 95 per cento. Nel ruolo attuale, riduzione del 18 per cento dei costi AWS e del 99 per cento della superficie di attacco della rete locale.',
    ],
    informal: [
      'Qualche numero concreto: in Oltrebase il refactoring ha tagliato il 40 per cento di downtime e alzato del 90 per cento la resilienza, ed erano circa 110.000 righe scritte nel primo anno. In Swatch il MES Logbook ha portato la qualita dei dati di produzione su del 95 per cento circa. Adesso in Openlogs: 18 per cento di costi AWS in meno e 99 per cento di superficie di attacco della rete locale in meno.',
    ],
  },
  {
    keywords: /architett|ddd|microserviz|solid|design|architecture/i,
    sources: ['SKILLS-06', 'WORK-SWATCH-01', 'WORK-SWATCH-03'],
    formal: [
      'Come Software Architect in Swatch Group ho guidato la progettazione di architetture modulari basate su Domain-Driven Design e microservizi, con rilasci gestiti tramite Azure DevOps. Sul MES Logbook 2.0 ho definito l architettura complessiva, il sistema di autenticazione compatibile con l infrastruttura aziendale e le specifiche di interfaccia in Figma, coordinando anche i partner esterni.',
    ],
    informal: [
      'Architettura per me vuol dire partire dal dominio: in Swatch ho guidato la progettazione modulare su DDD e microservizi. Sul MES Logbook 2.0 ho definito architettura e sistema di autenticazione, e ho anche disegnato le interfacce in Figma perche in fabbrica se la UI non segue il flusso reale dell operatore il dato te lo perdi.',
    ],
  },
  {
    keywords: /certificaz|corso|formazione|certificat|course|security|sicurezza/i,
    sources: ['CERT-01', 'CERT-08', 'MOTIVATION-02'],
    formal: [
      'Le certificazioni piu recenti sono concentrate su cloud e sicurezza: Azure Administrator Associate nel febbraio 2026, Introduction to Post-Quantum Cryptography nel gennaio 2026, un percorso su penetration testing e threat hunting nel febbraio 2026 e il certificato Trusted AI Safety Expert di Cloud Security Alliance nel luglio 2026. La sicurezza e un filo continuo dalla tesi di laurea sulle vulnerabilita della Carta Regionale dei Servizi.',
    ],
    informal: [
      'Nel 2025 e 2026 ho accelerato molto sulle certificazioni: Azure Administrator, crittografia post-quantistica, un percorso su penetration testing e threat hunting, e a luglio il TAISE sulla sicurezza dell AI. La sicurezza non e una virata recente: la mia tesi era sull implementazione di attacchi crittografici alla Carta Regionale dei Servizi, e finii sul giornale locale.',
    ],
  },
];

const GENERIC: Pick<MockAnswer, 'formal' | 'informal' | 'sources'> = {
  sources: ['IDENTITY-02', 'POSITIONING-01', 'ROLES-01'],
  formal: [
    'Sono Senior Software Engineer e ICT Architect, con circa quattordici anni di esperienza su fullstack, cloud, business intelligence, IoT e sicurezza. Il percorso va da una piattaforma IoT distribuita in Oltrebase, alla business intelligence in ATG, al retail internazionale in Ermenegildo Zegna, al manufacturing in Swatch Group, fino al ruolo attuale di ICT Architect in Openlogs.',
    'Il profilo copre l intero stack, dal protocollo di campo come Modbus e OPC fino all interfaccia utente, passando da database e API. Se vuole, posso entrare nel dettaglio di un ambito specifico: architettura, dati, cloud, sicurezza oppure il grado di match rispetto a una posizione.',
  ],
  informal: [
    'In due righe: sono un computer scientist con circa quattordici anni addosso, partito da una piattaforma IoT per impianti fotovoltaici e arrivato a fare l ICT Architect. In mezzo business intelligence, retail internazionale e manufacturing. Chiedimi quello che ti serve: architettura, dati, cloud, sicurezza, o quanto combacio con una posizione.',
    'Mi piace definirmi computer scientist nel senso ampio: mi interessa la teoria sotto la pratica ma il codice lo scrivo. Ho fatto IoT, BI, retail, manufacturing e ora infrastruttura e piattaforme dati. Su cosa vuoi che entri nel dettaglio?',
  ],
};

const chooseAnswer = (
  question: string,
  persona: ChatPersona
): { text: string; sources: string[] } => {
  const matching = ANSWERS.filter((a) => a.keywords.test(question));
  const chosen = matching.length > 0 ? pick(matching) : null;
  const pool = chosen ?? GENERIC;
  const variants = persona === 'formal' ? pool.formal : pool.informal;
  return { text: pick(variants), sources: pool.sources };
};

// --------------------------------------------------------------------------- //
// API simulata
// --------------------------------------------------------------------------- //

export async function mockWarmup(signal?: AbortSignal): Promise<boolean> {
  assertNotProduction();
  try {
    await sleep(jitter(400, 200), signal);
    return true;
  } catch {
    return false;
  }
}

export async function mockFetchMeta(signal?: AbortSignal): Promise<ChatMeta | null> {
  assertNotProduction();
  try {
    await sleep(jitter(250, 150), signal);
  } catch {
    return null;
  }

  return {
    subject: 'Luca Astolfi',
    contact_url: 'https://www.linkedin.com/in/luca-astolfi-87238745/',
    suggested_questions: [],
    rate_limit: `1/${MOCK_RATE_LIMIT_WINDOW_SECONDS}s`,
    // Finestra corta di proposito: il cooldown reale e' di un minuto, che in
    // sviluppo renderebbe la chat inutilizzabile. Dieci secondi mostrano il
    // countdown senza ostacolare le prove.
    rate_limit_window_seconds: MOCK_RATE_LIMIT_WINDOW_SECONDS,
    budget: {
      month: new Date().toISOString().slice(0, 7),
      used_tokens: 12_500,
      cap_tokens: 1_000_000,
      remaining_tokens: 987_500,
      exhausted: false,
      persistent: false,
    },
    max_input_chars: 3000,
  };
}

/** Numero di messaggi già serviti: il primo paga il cold start simulato. */
let servedCount = 0;

export const resetMockSession = (): void => {
  servedCount = 0;
};

export async function mockStreamChat({
  messages,
  persona,
  signal,
  onEvent,
}: StreamChatOptions): Promise<void> {
  assertNotProduction();

  const question = messages[messages.length - 1]?.content ?? '';
  const command = /^\/(slow|deny|filter|limit|error|budget)\b/i.exec(question.trim());
  const directive = command?.[1]?.toLowerCase();

  const isFirst = servedCount === 0;
  servedCount += 1;

  if (directive === 'limit') {
    await sleep(jitter(300, 100), signal);
    throw new ChatError(
      `Una domanda ogni ${MOCK_RATE_LIMIT_WINDOW_SECONDS} secondi in modalita demo. Riprova a breve.`,
      'rate_limited',
      MOCK_RATE_LIMIT_WINDOW_SECONDS
    );
  }

  // Latenza iniziale: il primo messaggio simula il risveglio del container.
  const initialDelay = directive === 'slow' ? 9500 : isFirst ? coldStartMs() : jitter(700, 400);
  await sleep(initialDelay, signal);

  if (directive === 'budget') {
    emit(onEvent, {
      type: 'budget_exhausted',
      message:
        'Budget della demo mensile raggiunto (simulato). Per qualsiasi domanda sul mio profilo scrivimi su LinkedIn.',
    });
    emit(onEvent, { type: 'done' });
    return;
  }

  if (directive === 'deny') {
    emit(onEvent, {
      type: 'guard',
      decision: 'deny',
      categories: ['private_personal'],
      stage: 'semantic',
    });
    await streamText(
      'Questo non lo tratto: parlo solo del percorso professionale, delle competenze e dei progetti. Su orientamenti, salute, famiglia e opinioni personali non rispondo per scelta.',
      onEvent,
      signal
    );
    emit(onEvent, { type: 'done' });
    return;
  }

  const { text, sources } = chooseAnswer(question, persona);

  if (directive === 'filter') {
    emit(onEvent, {
      type: 'guard',
      decision: 'filter',
      categories: ['work_experience', 'private_personal'],
      stage: 'semantic',
    });
    emit(onEvent, {
      type: 'info',
      code: 'request_filtered',
      message:
        'Ho risposto solo alla parte professionale della domanda. Non trattata: opinione personale.',
      removed: ['personal opinion'],
    });
  } else {
    emit(onEvent, {
      type: 'guard',
      decision: 'allow',
      categories: ['work_experience'],
      stage: 'semantic',
    });
  }

  emit(onEvent, {
    type: 'sources',
    items: sources.map((id, index) => ({
      id,
      title: id,
      score: Number((0.82 - index * 0.06).toFixed(3)),
    })),
  });

  const streamed = await streamText(text, onEvent, signal, directive === 'error');

  if (directive === 'error' && streamed) {
    emit(onEvent, {
      type: 'error',
      code: 'generation_failed',
      message: 'La generazione della risposta e stata interrotta (simulato). Riprova tra poco.',
    });
    return;
  }

  emit(onEvent, { type: 'done' });
}

const emit = (onEvent: (event: StreamEvent) => void, event: StreamEvent): void => {
  onEvent(event);
};

/** Emette il testo parola per parola, con la cadenza di uno stream reale. */
async function streamText(
  text: string,
  onEvent: (event: StreamEvent) => void,
  signal: AbortSignal,
  truncateHalfway = false
): Promise<boolean> {
  const words = text.split(' ');
  const limit = truncateHalfway ? Math.ceil(words.length / 2) : words.length;

  for (let i = 0; i < limit; i += 1) {
    await sleep(jitter(32, 26), signal);
    onEvent({ type: 'token', text: words[i] + (i === words.length - 1 ? '' : ' ') });
  }
  return truncateHalfway;
}
